import React, { useEffect, useMemo, useState } from 'react'
import { Icon } from 'semantic-ui-react'
import { AppContainer } from '../../components/UI/Container'
import useTheme from '../../hooks/useTheme'
import AppFooter from '../Footers/AppFooter'
import { useResponsive } from '../UI/ResponsiveContext'
import AppText from '../UI/Texts/AppTexts'

// Konsistent färgpalett baserad på UI best practices
const STAFFI_RED = '#ff5a5a';
const STAFFI_LIGHT = '#fff5f5';
const STAFFI_GRADIENT = 'linear-gradient(135deg, #ff5a5a 0%, #ff7a7a 100%)';
const SOFT_SHADOW = '0 8px 32px rgba(255, 90, 90, 0.12)';
const HOVER_SHADOW = '0 16px 48px rgba(255, 90, 90, 0.20)';

// Sekundära färger - begränsad palett
const SUCCESS_COLOR = '#10b981'; // Grön för framgång/positiva element
const WARNING_COLOR = '#f59e0b'; // Gul för varningar/mellansteg
const INFO_COLOR = '#3b82f6';    // Blå för information
const NEUTRAL_COLOR = '#6b7280'; // Grå för neutral information

// Enhanced FAQ data med mer detaljerade svar
const FAQ_DATA = [
    {
        question: "Är Staffi gratis att använda?",
        answer: "Ja, Staffi är helt gratis för vårdpersonal. Inga dolda avgifter, inga månadsavgifter. Vi tjänar pengar från vårdgivare och bemanningsföretag som använder vår tjänst för att nå kvalificerade kandidater och förenkla sin rekryteringsprocess.",
        icon: "money bill alternate outline"
    },
    {
        question: "Vilka typer av uppdrag finns på Staffi?",
        answer: "Vi har uppdrag inom hela vård- och omsorgssektorn - från akutsjukvård till äldreomsorg, både korta vikariat och längre uppdrag. Allt från timmar till månader.",
        icon: "hospital outline"
    },
    {
        question: "Hur säkerställer ni kvaliteten på uppdragen?",
        answer: "Alla bemanningsföretag genomgår en rigorös verifieringsprocess. Vi granskar företagens legitimitet och tidigare omdömen innan de får annonsera.",
        icon: "shield alternate"
    },
    {
        question: "Varför visar ni ersättning per timme och inte lön?",
        answer: "I dagsläget visar vi ersättningar exklusive moms som riktar sig till konsulter som fakturerar via eget företag. En lön kan inkludera andra aspekter som resa och boende som är svåra att beräkna på förhand. Vi arbetar för att snart även kunna visa bruttolön per timme. Håll ut!",
        icon: "eye"
    },
    {
        question: "Hur snabbt kan jag få svar på mina ansökningar?",
        answer: "De flesta bemanningsföretag svarar inom 24-48 timmar. Du kan följa statusen på din ansökan live i appen och få notifikationer när något händer.",
        icon: "clock outline"
    },
    {
        question: "Behöver jag ladda upp CV och dokument för varje ansökan?",
        answer: "Nej! Ladda upp dina dokument en gång så återanvänds de automatiskt för framtida ansökningar. Du kan alltid uppdatera eller lägga till nya dokument.",
        icon: "file alternate outline"
    }
];

// Modern Breadcrumb component med förbättrad styling
const Breadcrumb = () => {
    const { isMobile } = useResponsive();
    const theme = useTheme();
    return (
        <nav aria-label="Sökväg" role="navigation" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderBottom: '1px solid rgba(255, 90, 90, 0.1)',
            padding: `16px ${isMobile ? theme.spacing.elementPadding : theme.spacing.contentPaddingBig} 20px`,
            backdropFilter: 'blur(10px)'
        }}>
            <div style={{ maxWidth: 1200, width: '100%' }}>
                <ol style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    margin: 0,
                    padding: 0,
                    listStyle: 'none'
                }} itemScope itemType="https://schema.org/BreadcrumbList">
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <a href="/" style={{
                            color: '#6c757d',
                            textDecoration: 'none',
                            fontSize: 15,
                            fontWeight: 500,
                            padding: '8px 12px',
                            borderRadius: 12,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }}
                            itemProp="item"
                            onMouseEnter={e => {
                                e.target.style.color = STAFFI_RED;
                                e.target.style.background = STAFFI_LIGHT;
                            }}
                            onMouseLeave={e => {
                                e.target.style.color = '#6c757d';
                                e.target.style.background = 'transparent';
                            }}
                            aria-label="Gå till startsidan"
                        >
                            <Icon name="home" size="small" />
                            <span itemProp="name">Hem</span>
                        </a>
                        <meta itemProp="position" content="1" />
                    </li>
                    <li style={{ color: '#dee2e6', fontSize: 14 }} aria-hidden="true">
                        <Icon name="chevron right" size="small" />
                    </li>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                        <span style={{
                            color: STAFFI_RED,
                            fontSize: 15,
                            fontWeight: 600,
                            padding: '8px 12px',
                            background: STAFFI_LIGHT,
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                        }} itemProp="name" aria-current="page">
                            <Icon name="info circle" size="small" />
                            <span>Om oss</span>
                        </span>
                        <meta itemProp="position" content="2" />
                    </li>
                </ol>
            </div>
        </nav>
    );
};

// Modern FAQ Item Component
const ModernFAQItem = ({ question, answer, icon, index }) => {
    const { isMobile } = useResponsive();
    const [isOpen, setIsOpen] = useState(false);

    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    const color = colors[index % colors.length];

    return (
        <div style={{
            background: '#fff',
            borderRadius: 20,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
        }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    width: '100%',
                    padding: isMobile ? '24px 20px' : '28px 24px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    transition: 'all 0.3s ease'
                }}
                aria-expanded={isOpen}
                onMouseEnter={e => {
                    e.currentTarget.style.background = '#f8fafc';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.background = 'none';
                }}
            >
                {/* Icon */}
                <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                }}>
                    <Icon
                        name={icon}
                        style={{
                            color: color,
                            fontSize: 18
                        }}
                    />
                </div>

                {/* Question */}
                <div style={{
                    flex: 1,
                    textAlign: 'left',
                    fontSize: isMobile ? 16 : 17,
                    fontWeight: 600,
                    color: '#1a1a1a',
                    lineHeight: 1.4
                }}>
                    {question}
                </div>

                {/* Expand icon */}
                <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: isOpen ? `${color}20` : '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    flexShrink: 0
                }}>
                    <Icon
                        name={isOpen ? 'minus' : 'plus'}
                        style={{
                            color: isOpen ? color : '#6b7280',
                            fontSize: 14,
                            transition: 'all 0.3s ease'
                        }}
                    />
                </div>
            </button>

            {/* Answer */}
            <div style={{
                maxHeight: isOpen ? '200px' : '0',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <div style={{
                    padding: isMobile ? '0 20px 24px 84px' : '0 24px 28px 88px',
                    fontSize: isMobile ? 15 : 16,
                    lineHeight: 1.6,
                    color: '#4a5568'
                }}>
                    {answer}
                </div>
            </div>
        </div>
    );
};
const ModernBenefitCard = ({ icon, title, description, color, features }) => {
    const { isMobile } = useResponsive();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <article
            style={{
                background: '#fff',
                borderRadius: 24,
                padding: isMobile ? '32px 24px' : '40px 32px',
                boxShadow: isHovered ? '0 20px 48px rgba(0, 0, 0, 0.12)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isHovered ? 'translateY(-8px)' : 'translateY(0px)',
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Gradient accent */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)`,
                borderRadius: '24px 24px 0 0'
            }} />

            {/* Icon */}
            <div style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                background: `${color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
                transition: 'all 0.3s ease'
            }}>
                <Icon
                    name={icon}
                    size='big'
                    style={{
                        color: color,
                        transition: 'all 0.3s ease',
                        transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                    }}
                />
            </div>

            {/* Content */}
            <div style={{ flex: 1 }}>
                <h3 style={{
                    fontSize: isMobile ? 20 : 22,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    marginBottom: 12,
                    lineHeight: 1.3
                }}>
                    {title}
                </h3>

                <p style={{
                    fontSize: isMobile ? 16 : 17,
                    lineHeight: 1.6,
                    color: '#4a5568',
                    marginBottom: 24
                }}>
                    {description}
                </p>

                {/* Features list */}
                <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                }}>
                    {features.map((feature, index) => (
                        <li key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 8,
                            fontSize: 14,
                            color: '#6b7280'
                        }}>
                            <div style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                background: `${color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <Icon
                                    name="check"
                                    size="small"
                                    style={{ color: color, fontSize: 10 }}
                                />
                            </div>
                            {feature}
                        </li>
                    ))}
                </ul>
            </div>
        </article>
    );
};

const AboutPage = () => {
    const { isMobile, isTablet } = useResponsive();


    return (
        <>

            <main id="main-content" style={{ background: '#fff' }} role="main" aria-label="Om Staffi - huvudinnehåll">
                <Breadcrumb />

                {/* MODERN HERO SECTION */}
                <section
                    style={{
                        minHeight: isMobile ? '70vh' : '80vh',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #fff5f5 100%)',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        padding: isMobile ? '60px 0' : '80px 0'
                    }}
                    aria-labelledby="hero-heading"
                >
                    {/* Animated Background Elements */}
                    <div style={{
                        position: 'absolute',
                        top: '10%',
                        right: '10%',
                        width: 200,
                        height: 200,
                        background: 'radial-gradient(circle, rgba(255, 90, 90, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        animation: 'float 6s ease-in-out infinite',
                        zIndex: 0
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: '20%',
                        left: '5%',
                        width: 150,
                        height: 150,
                        background: 'radial-gradient(circle, rgba(255, 90, 90, 0.05) 0%, transparent 70%)',
                        borderRadius: '50%',
                        animation: 'float 8s ease-in-out infinite reverse',
                        zIndex: 0
                    }} />

                    <AppContainer style={{
                        maxWidth: 1200,
                        position: 'relative',
                        zIndex: 1,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            maxWidth: 800,
                            margin: '0 auto'
                        }}>
                            {/* Content Column */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isMobile ? 24 : 32,
                                width: '100%'
                            }}>
                                <div>
                                    <div style={{
                                        display: 'inline-block',
                                        background: STAFFI_GRADIENT,
                                        color: '#fff',
                                        padding: '8px 20px',
                                        borderRadius: 25,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        marginBottom: isMobile ? 20 : 24,
                                        boxShadow: SOFT_SHADOW
                                    }}>
                                        ✨ Framtiden för vårdrekrytering
                                    </div>

                                    <AppText.HeadingBig
                                        use='h1'
                                        id="hero-heading"
                                        lang="sv"
                                        style={{
                                            fontSize: isMobile ? '2.5rem' : isTablet ? '3.5rem' : '4rem',
                                            fontWeight: 900,
                                            lineHeight: 1.1,
                                            color: '#1a1a1a',
                                            marginBottom: isMobile ? 48 : 64,
                                            letterSpacing: '-0.02em'
                                        }}
                                    >
                                        Sluta jaga.{' '}
                                        <span style={{
                                            background: STAFFI_GRADIENT,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text'
                                        }}>
                                            Börja välja.
                                        </span>
                                    </AppText.HeadingBig>

                                    <AppText.BodyText
                                        use='p'
                                        style={{
                                            fontSize: isMobile ? 18 : isTablet ? 20 : 22,
                                            lineHeight: 1.6,
                                            color: '#4a5568',
                                            marginTop: isMobile ? 8 : 16,
                                            marginBottom: isMobile ? 32 : 40,
                                            fontWeight: 400,
                                            maxWidth: '100%'
                                        }}
                                    >
                                        Jämför uppdrag. Ansök med ett klick.
                                        Ingen mer gissning – bara kristallklar information och sjyssta villkor att lita på.
                                    </AppText.BodyText>
                                </div>

                                {/* CTA Buttons */}
                                <div style={{
                                    display: 'flex',
                                    gap: 16,
                                    flexDirection: isMobile ? 'column' : 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <a
                                        href="/register"
                                        style={{
                                            background: STAFFI_GRADIENT,
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 16,
                                            fontWeight: 700,
                                            fontSize: 18,
                                            padding: '18px 36px',
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            boxShadow: SOFT_SHADOW,
                                            transition: 'all 0.3s ease',
                                            textAlign: 'center',
                                            minWidth: isMobile ? '100%' : 200
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = HOVER_SHADOW;
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.transform = 'translateY(0px)';
                                            e.target.style.boxShadow = SOFT_SHADOW;
                                        }}
                                    >
                                        <Icon name="rocket" />
                                        Kom igång gratis
                                    </a>

                                    <a
                                        href="#benefits"
                                        style={{
                                            background: '#fff',
                                            color: STAFFI_RED,
                                            border: `2px solid ${STAFFI_RED}`,
                                            borderRadius: 16,
                                            fontWeight: 600,
                                            fontSize: 18,
                                            padding: '16px 36px',
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            transition: 'all 0.3s ease',
                                            textAlign: 'center',
                                            minWidth: isMobile ? '100%' : 200
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.background = STAFFI_RED;
                                            e.target.style.color = '#fff';
                                            e.target.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.background = '#fff';
                                            e.target.style.color = STAFFI_RED;
                                            e.target.style.transform = 'translateY(0px)';
                                        }}
                                    >
                                        <Icon name="arrow down" />
                                        Läs mer
                                    </a>
                                </div>

                                {/* Trust indicators */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: isMobile ? 16 : 24,
                                    marginTop: 20,
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        color: '#6b7280',
                                        fontSize: 14
                                    }}>
                                        <Icon name="check circle" style={{ color: SUCCESS_COLOR }} />
                                        Alltid gratis
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        color: '#6b7280',
                                        fontSize: 14
                                    }}>
                                        <Icon name="shield alternate" style={{ color: SUCCESS_COLOR }} />
                                        Strävar efter transparens
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        color: '#6b7280',
                                        fontSize: 14
                                    }}>
                                        <Icon name="clock" style={{ color: SUCCESS_COLOR }} />
                                        Snabba svar
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* Vad är Staffi sektion */}
                <section
                    style={{
                        padding: isMobile ? '60px 0' : '80px 0',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f0f9ff 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="what-is-staffi-heading"
                >
                    <AppContainer style={{
                        maxWidth: 1000,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            borderRadius: 32,
                            padding: isMobile ? '40px 24px' : '60px 48px',
                            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.06)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Subtil bakgrundsdekoration */}
                            <div style={{
                                position: 'absolute',
                                top: -50,
                                right: -50,
                                width: 100,
                                height: 100,
                                background: 'radial-gradient(circle, rgba(255, 90, 90, 0.05) 0%, transparent 70%)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    display: 'inline-block',
                                    background: STAFFI_LIGHT,
                                    color: STAFFI_RED,
                                    padding: '8px 20px',
                                    borderRadius: 25,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    marginBottom: isMobile ? 20 : 24
                                }}>
                                    💡 Vad är Staffi?
                                </div>

                                <AppText.SubHeading
                                    use='h2'
                                    id="what-is-staffi-heading"
                                    style={{
                                        fontSize: isMobile ? '2rem' : '2.5rem',
                                        fontWeight: 700,
                                        color: '#1a1a1a',
                                        marginBottom: isMobile ? 64 : 80,
                                        letterSpacing: '-0.01em'
                                    }}
                                >
                                    En{' '}
                                    <span style={{
                                        background: STAFFI_GRADIENT,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text'
                                    }}>
                                        oberoende portal
                                    </span>
                                    {' '}för uppdrag inom vård- och omsorg
                                </AppText.SubHeading>

                                <AppText.BodyText
                                    style={{
                                        fontSize: isMobile ? 18 : 20,
                                        lineHeight: 1.7,
                                        color: '#4a5568',
                                        maxWidth: 800,
                                        margin: '0 auto 32px',
                                        fontWeight: 400
                                    }}
                                >
                                    På Staffi kan man enkelt kan hitta, jämföra och ansöka uppdrag. Vi är måna om att alla uppdrag på Staffi har tydliga villkor och ersättning. Staffi samarbetar med vårdgivare och bemanningsföretag som prioriterar transparens och kvalitet i rekryteringsprocessen.
                                </AppText.BodyText>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                                    gap: isMobile ? 24 : 32,
                                    marginTop: isMobile ? 32 : 40
                                }}>
                                    <div style={{
                                        padding: isMobile ? '20px 16px' : '24px 20px',
                                        background: 'rgba(255, 90, 90, 0.05)',
                                        borderRadius: 16,
                                        border: '1px solid rgba(255, 90, 90, 0.1)'
                                    }}>
                                        <Icon name="shield alternate" style={{
                                            color: STAFFI_RED,
                                            fontSize: '1.5rem',
                                            marginBottom: 8
                                        }} />
                                        <div style={{
                                            fontSize: isMobile ? 16 : 17,
                                            fontWeight: 600,
                                            color: '#1a1a1a',
                                            marginBottom: 4
                                        }}>
                                            Oberoende
                                        </div>
                                        <div style={{
                                            fontSize: 14,
                                            color: '#6b7280',
                                            lineHeight: 1.5
                                        }}>
                                            Vi är inte ett bemanningsbolag utan en neutral plattform
                                        </div>
                                    </div>

                                    <div style={{
                                        padding: isMobile ? '20px 16px' : '24px 20px',
                                        background: 'rgba(59, 130, 246, 0.05)',
                                        borderRadius: 16,
                                        border: '1px solid rgba(59, 130, 246, 0.1)'
                                    }}>
                                        <Icon name="handshake outline" style={{
                                            color: INFO_COLOR,
                                            fontSize: '1.5rem',
                                            marginBottom: 8
                                        }} />
                                        <div style={{
                                            fontSize: isMobile ? 16 : 17,
                                            fontWeight: 600,
                                            color: '#1a1a1a',
                                            marginBottom: 4
                                        }}>
                                            Samarbete
                                        </div>
                                        <div style={{
                                            fontSize: 14,
                                            color: '#6b7280',
                                            lineHeight: 1.5
                                        }}>
                                            Arbetar med rekryterare och vårdgivare för bästa resultat
                                        </div>
                                    </div>

                                    <div style={{
                                        padding: isMobile ? '20px 16px' : '24px 20px',
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        borderRadius: 16,
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                    }}>
                                        <Icon name="eye" style={{
                                            color: SUCCESS_COLOR,
                                            fontSize: '1.5rem',
                                            marginBottom: 8
                                        }} />
                                        <div style={{
                                            fontSize: isMobile ? 16 : 17,
                                            fontWeight: 600,
                                            color: '#1a1a1a',
                                            marginBottom: 4
                                        }}>
                                            Transparens
                                        </div>
                                        <div style={{
                                            fontSize: 14,
                                            color: '#6b7280',
                                            lineHeight: 1.5
                                        }}>
                                            Strävar efter tydliga villkor och ersättning när möjligt
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* Problem sektion */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="problem-heading"
                >
                    {/* Background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '20%',
                        left: '-10%',
                        width: 300,
                        height: 300,
                        background: 'radial-gradient(circle, rgba(255, 90, 90, 0.03) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }} />

                    <AppContainer style={{
                        maxWidth: 1200,
                        position: 'relative',
                        zIndex: 1,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: isMobile ? 60 : 80 }}>
                            <div style={{
                                display: 'inline-block',
                                background: 'rgba(255, 90, 90, 0.1)',
                                color: STAFFI_RED,
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 20 : 24
                            }}>
                                😤 Problemet
                            </div>

                            <AppText.SubHeading
                                use='h2'
                                id="problem-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: '#1a1a1a',
                                    marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Känner du igen dig?
                            </AppText.SubHeading>

                        </div>

                        {/* Story section */}
                        <div style={{
                            background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
                            borderRadius: 32,
                            padding: isMobile ? '40px 24px' : '60px 48px',
                            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.06)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            textAlign: 'center'
                        }}>
                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 22,
                                    lineHeight: 1.7,
                                    color: '#374151',
                                    fontStyle: 'italic',
                                    marginBottom: 24
                                }}
                            >
                                "Ping i mobilen. Konsultchefen undrar om din tillgänlighet. Du får några förslag,
                                men är det verkligen de bästa? Ersättning? Arbetsmiljö? Tja, det är lite som att
                                köpa grisen i säcken. Sen börjar pappersjakten... och väntan. Och väntan. Okej, så
                                jag får uppdraget! Så till avtalet. Men vi sa väl inget om jourer..."
                            </AppText.BodyText>

                            <div style={{
                                fontSize: 14,
                                color: '#9ca3af',
                                fontWeight: 500
                            }}>
                                – Bekant historia för vårdpersonal överallt
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* SOLUTION SECTION - COMPLETELY REDESIGNED */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: STAFFI_GRADIENT,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                    aria-labelledby="solution-heading"
                >
                    {/* Animated background elements */}
                    <div style={{
                        position: 'absolute',
                        top: '10%',
                        right: '-5%',
                        width: 400,
                        height: 400,
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        animation: 'float 10s ease-in-out infinite',
                        zIndex: 0
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: '20%',
                        left: '-10%',
                        width: 300,
                        height: 300,
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
                        borderRadius: '50%',
                        animation: 'float 12s ease-in-out infinite reverse',
                        zIndex: 0
                    }} />

                    <AppContainer style={{
                        maxWidth: 1200,
                        position: 'relative',
                        zIndex: 1,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: isMobile ? 60 : 100,
                            alignItems: 'center'
                        }}>
                            {/* Content Column */}
                            <div>
                                <div style={{
                                    display: 'inline-block',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    color: '#fff',
                                    padding: '8px 20px',
                                    borderRadius: 25,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    marginBottom: isMobile ? 20 : 24,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    ✨ Lösningen
                                </div>

                                <AppText.SubHeading
                                    use='h2'
                                    id="solution-heading"
                                    style={{
                                        fontSize: isMobile ? '2.5rem' : '3.5rem',
                                        fontWeight: 800,
                                        lineHeight: 1.2,
                                        color: '#fff',
                                        marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    Dags för en ny story.{' '}
                                    <span style={{
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        padding: '0 16px',
                                        borderRadius: 16,
                                        display: isMobile ? 'block' : 'inline'
                                    }}>
                                        Din story.
                                    </span>
                                </AppText.SubHeading>

                                <AppText.BodyText
                                    style={{
                                        fontSize: isMobile ? 18 : 22,
                                        lineHeight: 1.7,
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        marginBottom: isMobile ? 40 : 48
                                    }}
                                >
                                    Staffi vänder på steken. Du ser alla uppdrag, alla villkor, alla ersättningar – svart på vitt.
                                    Du väljer, du styr, du slipper gissa och anta. Full kontroll för dig.
                                </AppText.BodyText>

                                {/* Key benefits list */}
                                <div style={{ marginBottom: isMobile ? 40 : 48 }}>
                                    {[
                                        { icon: 'eye', text: 'Strävar efter transparens i alla uppdrag' },
                                        { icon: 'lightning', text: 'Ansök med ett klick' },
                                        { icon: 'chart line', text: 'Följ din ansökan live' }
                                    ].map((item, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                            marginBottom: 16,
                                            color: '#fff'
                                        }}>
                                            <div style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backdropFilter: 'blur(10px)'
                                            }}>
                                                <Icon name={item.icon} style={{ color: '#fff' }} />
                                            </div>
                                            <span style={{
                                                fontSize: isMobile ? 16 : 18,
                                                fontWeight: 500
                                            }}>
                                                {item.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <a
                                    href="/register"
                                    style={{
                                        background: '#fff',
                                        color: STAFFI_RED,
                                        border: 'none',
                                        borderRadius: 16,
                                        fontWeight: 700,
                                        fontSize: 18,
                                        padding: '18px 36px',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={e => {
                                        e.target.style.transform = 'translateY(-3px)';
                                        e.target.style.boxShadow = '0 16px 48px rgba(0, 0, 0, 0.3)';
                                    }}
                                    onMouseLeave={e => {
                                        e.target.style.transform = 'translateY(0px)';
                                        e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
                                    }}
                                >
                                    <Icon name="rocket" />
                                    Prova nu - gratis
                                </a>
                            </div>

                            {/* Visual Column - Modern Phone Mockup */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <div style={{
                                    position: 'relative',
                                    width: isMobile ? 280 : 320,
                                    height: isMobile ? 560 : 640,
                                    background: 'linear-gradient(145deg, #1f2937 0%, #111827 100%)',
                                    borderRadius: 48,
                                    padding: 8,
                                    boxShadow: '0 32px 80px rgba(0, 0, 0, 0.4)'
                                }}>
                                    {/* Phone screen */}
                                    <div style={{
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                                        borderRadius: 40,
                                        padding: 24,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Header */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: 24,
                                            padding: '12px 0'
                                        }}>
                                            <div style={{
                                                fontSize: 18,
                                                fontWeight: 700,
                                                color: STAFFI_RED
                                            }}>
                                                Staffi
                                            </div>
                                            <div style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                background: STAFFI_LIGHT,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Icon name="user" style={{ color: STAFFI_RED }} />
                                            </div>
                                        </div>

                                        {/* Job Cards */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {[
                                                { title: 'Läkare', hospital: 'Karolinska', pay: '1815 kr/h', status: 'Ny!' },
                                                { title: 'Akutsjuksköterska', hospital: 'Danderyds', pay: '648 kr/h', status: 'Svar idag' },
                                                { title: 'Undersköterska', hospital: 'S:t Göran', pay: '380 kr/h', status: 'Intresseanmälan' },
                                                { title: 'Barnmorska', hospital: 'Södersjukhuset', pay: '691 kr/h', status: 'Ansökt' }
                                            ].map((job, index) => (
                                                <div key={index} style={{
                                                    background: '#fff',
                                                    borderRadius: 16,
                                                    padding: 16,
                                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                                                    border: '1px solid rgba(255, 90, 90, 0.1)'
                                                }}>
                                                    <div style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        marginBottom: 8
                                                    }}>
                                                        <div>
                                                            <div style={{
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                                color: '#1a1a1a',
                                                                marginBottom: 4
                                                            }}>
                                                                {job.title}
                                                            </div>
                                                            <div style={{
                                                                fontSize: 12,
                                                                color: '#6b7280'
                                                            }}>
                                                                {job.hospital}
                                                            </div>
                                                        </div>
                                                        <div style={{
                                                            background: index === 0 ? '#10b981' : index === 1 ? '#f59e0b' : index === 2 ? STAFFI_RED : '#8b5cf6',
                                                            color: '#fff',
                                                            padding: '4px 8px',
                                                            borderRadius: 8,
                                                            fontSize: 10,
                                                            fontWeight: 600
                                                        }}>
                                                            {job.status}
                                                        </div>
                                                    </div>
                                                    <div style={{
                                                        fontSize: 16,
                                                        fontWeight: 700,
                                                        color: STAFFI_RED
                                                    }}>
                                                        {job.pay}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Phone highlight effect */}
                                    <div style={{
                                        position: 'absolute',
                                        top: -20,
                                        left: -20,
                                        right: -20,
                                        bottom: -20,
                                        background: 'conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                                        borderRadius: 60,
                                        animation: 'rotate 8s linear infinite',
                                        zIndex: -1
                                    }} />
                                </div>
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* BENEFITS SECTION - REDESIGNED */}
                <section
                    id="benefits"
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="benefits-heading"
                >
                    <AppContainer style={{
                        maxWidth: 1200,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: isMobile ? 60 : 80 }}>
                            <div style={{
                                display: 'inline-block',
                                background: STAFFI_LIGHT,
                                color: STAFFI_RED,
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 20 : 24
                            }}>
                                🚀 Fördelarna
                            </div>

                            <AppText.SubHeading
                                use='h2'
                                id="benefits-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: '#1a1a1a',
                                    marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Vad händer när{' '}
                                <span style={{
                                    background: STAFFI_GRADIENT,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    du styr?
                                </span>
                            </AppText.SubHeading>

                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 20,
                                    lineHeight: 1.6,
                                    color: '#4a5568',
                                    maxWidth: 700,
                                    margin: '0 auto'
                                }}
                            >
                                När du tar kontrollen händer det magiska saker. Här är vad som väntar.
                            </AppText.BodyText>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: isMobile ? 32 : 40,
                            marginBottom: isMobile ? 60 : 80
                        }}>
                            <ModernBenefitCard
                                icon='money bill alternate outline'
                                title='Strävar efter tydlig ersättning'
                                description='Vi arbetar för att visa vad du får betalt när det är möjligt. Inga dolda avgifter när ersättning visas.'
                                color={SUCCESS_COLOR}
                                features={['Angiven timersättning när möjligt', 'OB-tillägg synliga', 'Reseersättning tydlig']}
                            />
                            <ModernBenefitCard
                                icon='search'
                                title='Smart jämförelse'
                                description='Filtrera och jämför uppdrag baserat på vad som är viktigt för dig.'
                                color={INFO_COLOR}
                                features={['Arbetsplatsen ⭐️⭐️⭐️⭐️⭐️', 'Filtrera efter ersättning', 'Journalsystem']}
                            />
                            <ModernBenefitCard
                                icon='chart line'
                                title='Live-spårning'
                                description='Följ din ansökan i realtid. Se exakt var du står i processen.'
                                color={STAFFI_RED}
                                features={['Statusuppdateringar', 'chatta med konsultchefen', 'Bli först']}
                            />
                            <ModernBenefitCard
                                icon='file alternate outline'
                                title='En gång - alltid klar'
                                description='Ladda upp dokument en gång, använd för alla framtida ansökningar.'
                                color={WARNING_COLOR}
                                features={['Auto-ifylling', 'Säker lagring', 'Snabb ansökan']}
                            />
                        </div>
                    </AppContainer>
                </section>

                {/* STAFFIS ERSÄTTNINGSMODELL - NY SEKTION */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #f8fafc 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="compensation-heading"
                >
                    {/* Background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '15%',
                        right: '-5%',
                        width: 250,
                        height: 250,
                        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }} />

                    <AppContainer style={{
                        maxWidth: 1200,
                        position: 'relative',
                        zIndex: 1,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: isMobile ? 60 : 80 }}>
                            <div style={{
                                display: 'inline-block',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#fff',
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 20 : 24,
                                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.2)'
                            }}>
                                💰 Ersättningsmodell
                            </div>

                            <AppText.SubHeading
                                use='h2'
                                id="compensation-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: '#1a1a1a',
                                    marginBottom: isMobile ? 24 : 32,
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Staffis ambition:{' '}
                                <span style={{
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    tydlig och hög ersättning
                                </span>
                            </AppText.SubHeading>

                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 20,
                                    lineHeight: 1.6,
                                    color: '#4a5568',
                                    maxWidth: 800,
                                    margin: '0 auto'
                                }}
                            >
                                Vi arbetar aktivt för att ersättningen ska vara både transparent och konkurrenskraftig.
                                För våra mest aktiva konsulter har vi skapat ett bonussystem som belönar lojalitet och engagemang.
                            </AppText.BodyText>
                        </div>

                        {/* Ersättningsprinciper */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: isMobile ? 32 : 48,
                            marginBottom: isMobile ? 60 : 80
                        }}>
                            {/* Vänster kolumn - Principer */}
                            <div style={{
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                borderRadius: 24,
                                padding: isMobile ? '32px 24px' : '40px 32px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                                border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                                <div style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 16,
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: 24
                                }}>
                                    <Icon name="target" size="large" style={{ color: '#fff' }} />
                                </div>

                                <h3 style={{
                                    fontSize: isMobile ? 20 : 24,
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    marginBottom: 16
                                }}>
                                    Våra ersättningsprinciper
                                </h3>

                                <ul style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0
                                }}>
                                    {[
                                        'Transparens - du ser ersättningen direkt när det är möjligt',
                                        'Konkurrenskraft - vi strävar efter marknadsmässiga nivåer',
                                        'Rättvisa - samma ersättning för samma kompetens och erfarenhet',
                                        'Kontinuitet - belönar långsiktiga partnerskap'
                                    ].map((principle, index) => (
                                        <li key={index} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 12,
                                            marginBottom: 16,
                                            fontSize: isMobile ? 15 : 16,
                                            lineHeight: 1.6,
                                            color: '#4a5568'
                                        }}>
                                            <div style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                marginTop: 2
                                            }}>
                                                <Icon name="check" size="small" style={{ color: '#fff', fontSize: 10 }} />
                                            </div>
                                            {principle}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Höger kolumn - Bonussystem */}
                            <div style={{
                                background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                                borderRadius: 24,
                                padding: isMobile ? '32px 24px' : '40px 32px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                                border: '1px solid rgba(16, 185, 129, 0.1)'
                            }}>
                                <div style={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 16,
                                    background: `linear-gradient(135deg, ${INFO_COLOR} 0%, #1d4ed8 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: 24
                                }}>
                                    <Icon name="trophy" size="large" style={{ color: '#fff' }} />
                                </div>

                                <h3 style={{
                                    fontSize: isMobile ? 20 : 24,
                                    fontWeight: 700,
                                    color: '#1a1a1a',
                                    marginBottom: 16
                                }}>
                                    Bonustrappa för aktiva konsulter
                                </h3>

                                <p style={{
                                    fontSize: isMobile ? 15 : 16,
                                    lineHeight: 1.6,
                                    color: '#4a5568',
                                    marginBottom: 24
                                }}>
                                    För konsulter som tar flera uppdrag via Staffi ökar ersättningen baserat på antal arbetade timmar:
                                </p>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {[
                                        { level: 'Nivå 1', hours: '0-160 timmar', bonus: 'Hög grundersättning', color: INFO_COLOR, icon: 'star outline' },
                                        { level: 'Nivå 2', hours: '161-320 timmar', bonus: 'Bonus +1-2%', color: WARNING_COLOR, icon: 'star half full' },
                                        { level: 'Nivå 3', hours: '321+ timmar', bonus: 'Bonus +2-5%', color: SUCCESS_COLOR, icon: 'star' }
                                    ].map((tier, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                            padding: '16px 20px',
                                            background: index === 2 ? `rgba(16, 185, 129, 0.1)` : index === 1 ? `rgba(245, 158, 11, 0.1)` : `rgba(59, 130, 246, 0.1)`,
                                            borderRadius: 16,
                                            border: `1px solid ${tier.color}20`
                                        }}>
                                            <div style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: `${tier.color}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Icon name={tier.icon} style={{ color: tier.color, fontSize: '16px' }} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    fontSize: 16,
                                                    fontWeight: 600,
                                                    color: '#1a1a1a',
                                                    marginBottom: 4
                                                }}>
                                                    {tier.level}
                                                </div>
                                                <div style={{
                                                    fontSize: 13,
                                                    color: '#6b7280',
                                                    marginBottom: 2
                                                }}>
                                                    {tier.hours}
                                                </div>
                                                <div style={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: tier.color
                                                }}>
                                                    {tier.bonus}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Call-to-action box */}
                        <div style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: 24,
                            padding: isMobile ? '32px 24px' : '40px 48px',
                            textAlign: 'center',
                            color: '#fff',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Background decoration */}
                            <div style={{
                                position: 'absolute',
                                top: -30,
                                right: -30,
                                width: 120,
                                height: 120,
                                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                                borderRadius: '50%',
                                zIndex: 0
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <h3 style={{
                                    fontSize: isMobile ? 20 : 24,
                                    fontWeight: 700,
                                    marginBottom: 12,
                                    color: '#fff'
                                }}>
                                    Redo att tjäna mer?
                                </h3>
                                <p style={{
                                    fontSize: isMobile ? 16 : 18,
                                    lineHeight: 1.6,
                                    marginBottom: 24,
                                    color: 'rgba(255, 255, 255, 0.9)'
                                }}>
                                    Börja med grundnivån och jobba dig upp i bonussystemet.
                                    Ju mer du arbetar via Staffi, desto mer tjänar du.
                                </p>
                                <a
                                    href="/register"
                                    style={{
                                        background: '#fff',
                                        color: '#059669',
                                        border: 'none',
                                        borderRadius: 16,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        padding: '16px 32px',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={e => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={e => {
                                        e.target.style.transform = 'translateY(0px)';
                                        e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                                    }}
                                >
                                    <Icon name="calculator" />
                                    Se aktuella uppdrag
                                </a>
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* HOW IT WORKS - MODERNIZED */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="how-it-works-heading"
                >
                    <AppContainer style={{
                        maxWidth: 1200,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: isMobile ? 60 : 80 }}>
                            <div style={{
                                display: 'inline-block',
                                background: `linear-gradient(135deg, ${SUCCESS_COLOR} 0%, #059669 100%)`,
                                color: '#fff',
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 20 : 24
                            }}>
                                ⚡ Så funkar det
                            </div>

                            <AppText.SubHeading
                                use='h2'
                                id="how-it-works-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: '#1a1a1a',
                                    marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Från nyfiken till{' '}
                                <span style={{
                                    background: `linear-gradient(135deg, ${SUCCESS_COLOR} 0%, #059669 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    accept
                                </span>
                            </AppText.SubHeading>

                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 20,
                                    lineHeight: 1.6,
                                    color: '#4a5568',
                                    maxWidth: 700,
                                    margin: '0 auto'
                                }}
                            >
                                Fem enkla steg från första intryck till att landa uppdrag.
                            </AppText.BodyText>
                        </div>

                        {/* Process Steps */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
                            gap: isMobile ? 40 : 32,
                            position: 'relative'
                        }}>
                            {/* Connection line for desktop */}
                            {!isMobile && (
                                <div style={{
                                    position: 'absolute',
                                    top: 40,
                                    left: '10%',
                                    right: '10%',
                                    height: 2,
                                    background: 'linear-gradient(90deg, #e5e7eb 0%, #d1d5db 50%, #e5e7eb 100%)',
                                    zIndex: 0
                                }} />
                            )}

                            {[
                                {
                                    step: '01',
                                    icon: 'user plus',
                                    title: 'Skapa profil',
                                    description: 'Registrera dig på 2 minuter. Lägg till dina kompetenser och preferenser.',
                                    color: INFO_COLOR
                                },
                                {
                                    step: '02',
                                    icon: 'search',
                                    title: 'Utforska uppdrag',
                                    description: 'Filtrera bland hundratals uppdrag. Se ersättning, villkor och arbetsplats direkt.',
                                    color: SUCCESS_COLOR
                                },
                                {
                                    step: '03',
                                    icon: 'paper plane',
                                    title: 'Ansök smart',
                                    description: 'Ett klick och din ansökan är skickad. Inget formulär-helvete.',
                                    color: STAFFI_RED
                                },
                                {
                                    step: '04',
                                    icon: 'chart line',
                                    title: 'Följ live',
                                    description: 'Se exakt var din ansökan står. Inga frågetecken, bara transparens.',
                                    color: WARNING_COLOR
                                },
                                {
                                    step: '05',
                                    icon: 'heart',
                                    title: 'Jobba lyckligt',
                                    description: 'Börja på ditt nya uppdrag med full koll på alla detaljer.',
                                    color: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 25%, #48dbfb 50%, #ff9ff3 75%, #54a0ff 100%)'
                                }
                            ].map((step, index) => (
                                <div key={index} style={{
                                    background: '#fff',
                                    borderRadius: 24,
                                    padding: isMobile ? '32px 24px' : '32px 20px',
                                    textAlign: 'center',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid rgba(0, 0, 0, 0.05)',
                                    position: 'relative',
                                    zIndex: 1,
                                    transition: 'all 0.3s ease',
                                    minHeight: isMobile ? 'auto' : 280
                                }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.transform = 'translateY(-8px)';
                                        e.currentTarget.style.boxShadow = '0 20px 48px rgba(0, 0, 0, 0.12)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.transform = 'translateY(0px)';
                                        e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)';
                                    }}
                                >
                                    {/* Step number */}
                                    <div style={{
                                        position: 'absolute',
                                        top: -16,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 32,
                                        height: 32,
                                        background: step.color.includes('gradient') ? step.color : step.color,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        boxShadow: step.color.includes('gradient') ? '0 4px 16px rgba(255, 107, 107, 0.4)' : `0 4px 16px ${step.color}40`
                                    }}>
                                        {step.step}
                                    </div>

                                    {/* Icon */}
                                    <div style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 16,
                                        background: step.color.includes('gradient') ? 'rgba(255, 107, 107, 0.15)' : `${step.color}15`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '24px auto 20px',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <Icon
                                            name={step.icon}
                                            size='large'
                                            style={{ color: step.color.includes('gradient') ? '#ff6b6b' : step.color }}
                                        />
                                    </div>

                                    <h3 style={{
                                        fontSize: isMobile ? 18 : 19,
                                        fontWeight: 700,
                                        color: '#1a1a1a',
                                        marginBottom: 12,
                                        lineHeight: 1.3
                                    }}>
                                        {step.title}
                                    </h3>

                                    <p style={{
                                        fontSize: isMobile ? 14 : 15,
                                        lineHeight: 1.6,
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        {step.description}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* CTA at bottom */}
                        <div style={{
                            textAlign: 'center',
                            marginTop: isMobile ? 60 : 80
                        }}>
                            <a
                                href="/register"
                                style={{
                                    background: `linear-gradient(135deg, ${SUCCESS_COLOR} 0%, #059669 100%)`,
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 16,
                                    fontWeight: 700,
                                    fontSize: 18,
                                    padding: '18px 36px',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={e => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 16px 48px rgba(16, 185, 129, 0.4)';
                                }}
                                onMouseLeave={e => {
                                    e.target.style.transform = 'translateY(0px)';
                                    e.target.style.boxShadow = '0 8px 32px rgba(16, 185, 129, 0.3)';
                                }}
                            >
                                <Icon name="play" />
                                Starta resan nu
                            </a>
                        </div>
                    </AppContainer>
                </section>

                {/* FAQ SECTION - NEW */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        position: 'relative'
                    }}
                    aria-labelledby="faq-heading"
                >
                    <AppContainer style={{
                        maxWidth: 1000,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: isMobile ? 60 : 80 }}>
                            <div style={{
                                display: 'inline-block',
                                background: `rgba(59, 130, 246, 0.1)`,
                                color: INFO_COLOR,
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 20 : 24
                            }}>
                                🤔 Vanliga frågor
                            </div>

                            <AppText.SubHeading
                                use='h2'
                                id="faq-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '3.5rem',
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: '#1a1a1a',
                                    marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Har du frågor?{' '}
                                <span style={{
                                    background: `linear-gradient(135deg, ${INFO_COLOR} 0%, #1d4ed8 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    Vi har svar!
                                </span>
                            </AppText.SubHeading>

                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 20,
                                    lineHeight: 1.6,
                                    color: '#4a5568',
                                    maxWidth: 700,
                                    margin: '0 auto'
                                }}
                            >
                                Här är svaren på de vanligaste frågorna från vårdpersonal.
                            </AppText.BodyText>
                        </div>

                        {/* FAQ Grid */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: isMobile ? 24 : 32
                        }}>
                            {FAQ_DATA.map((faq, index) => (
                                <ModernFAQItem
                                    key={index}
                                    question={faq.question}
                                    answer={faq.answer}
                                    icon={faq.icon}
                                    index={index}
                                />
                            ))}
                        </div>

                        {/* Contact CTA */}
                        <div style={{
                            textAlign: 'center',
                            marginTop: isMobile ? 60 : 80,
                            padding: isMobile ? '32px 24px' : '40px 48px',
                            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                            borderRadius: 24,
                            border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? 20 : 24,
                                fontWeight: 700,
                                color: '#1a1a1a',
                                marginBottom: 12
                            }}>
                                Har du fler frågor?
                            </h3>
                            <p style={{
                                fontSize: isMobile ? 16 : 17,
                                color: '#6b7280',
                                marginBottom: 24,
                                lineHeight: 1.6
                            }}>
                                Vårt team är här för att hjälpa dig. Kontakta oss så svarar vi inom 24 timmar.
                            </p>
                            <a
                                href="mailto:hej@staffi.se"
                                style={{
                                    background: `linear-gradient(135deg, ${INFO_COLOR} 0%, #1d4ed8 100%)`,
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 12,
                                    fontWeight: 600,
                                    fontSize: 16,
                                    padding: '14px 28px',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={e => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.3)';
                                }}
                                onMouseLeave={e => {
                                    e.target.style.transform = 'translateY(0px)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <Icon name="mail" />
                                Kontakta oss
                            </a>
                        </div>
                    </AppContainer>
                </section>

                {/* FINAL CTA - REDESIGNED */}
                <section
                    style={{
                        padding: isMobile ? '80px 0' : '120px 0',
                        background: STAFFI_GRADIENT,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                    aria-labelledby="final-cta-heading"
                >
                    {/* Background elements */}
                    <div style={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: 600,
                        height: 600,
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: '-20%',
                        left: '-10%',
                        width: 400,
                        height: 400,
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
                        borderRadius: '50%',
                        zIndex: 0
                    }} />

                    <AppContainer style={{
                        maxWidth: 800,
                        position: 'relative',
                        zIndex: 1,
                        padding: isMobile ? '0 20px' : '0 40px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                display: 'inline-block',
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: '#fff',
                                padding: '8px 20px',
                                borderRadius: 25,
                                fontSize: 14,
                                fontWeight: 600,
                                marginBottom: isMobile ? 24 : 32,
                                backdropFilter: 'blur(10px)'
                            }}>
                                🎉 Redo att börja?
                            </div>

                            <AppText.HeadingBig
                                use='h2'
                                id="final-cta-heading"
                                style={{
                                    fontSize: isMobile ? '2.5rem' : '4rem',
                                    fontWeight: 900,
                                    lineHeight: 1.1,
                                    color: '#fff',
                                    marginBottom: isMobile ? 64 : 80, // Mycket större marginal för tydlig separation
                                    letterSpacing: '-0.02em',
                                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                                }}
                            >
                                Byt manus.{' '}
                                <span style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    padding: '0 20px',
                                    borderRadius: 20,
                                    display: isMobile ? 'block' : 'inline'
                                }}>
                                    Välj Staffi.
                                </span>
                            </AppText.HeadingBig>

                            <AppText.BodyText
                                style={{
                                    fontSize: isMobile ? 18 : 22,
                                    lineHeight: 1.6,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    marginBottom: isMobile ? 40 : 48,
                                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                }}
                            >
                                Skapa konto och ta kontroll över konsultpusslet. Det är gratis, enkelt och faktiskt lite roligare.
                            </AppText.BodyText>

                            <div style={{
                                display: 'flex',
                                gap: isMobile ? 20 : 24,
                                flexDirection: isMobile ? 'column' : 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginBottom: isMobile ? 40 : 48
                            }}>
                                <a
                                    href="/register"
                                    style={{
                                        background: '#fff',
                                        color: STAFFI_RED,
                                        border: 'none',
                                        borderRadius: 16,
                                        fontWeight: 700,
                                        fontSize: isMobile ? 18 : 20,
                                        padding: isMobile ? '18px 36px' : '20px 48px',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                                        transition: 'all 0.3s ease',
                                        minWidth: isMobile ? '100%' : 'auto'
                                    }}
                                    onMouseEnter={e => {
                                        e.target.style.transform = 'translateY(-3px)';
                                        e.target.style.boxShadow = '0 16px 48px rgba(0, 0, 0, 0.3)';
                                        e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                                    }}
                                    onMouseLeave={e => {
                                        e.target.style.transform = 'translateY(0px)';
                                        e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.2)';
                                        e.target.style.background = '#fff';
                                    }}
                                >
                                    <Icon name="rocket" />
                                    Registrera dig gratis
                                </a>

                                <a
                                    href="/"
                                    style={{
                                        background: 'transparent',
                                        color: '#fff',
                                        border: '2px solid rgba(255, 255, 255, 0.8)',
                                        borderRadius: 16,
                                        fontWeight: 600,
                                        fontSize: isMobile ? 16 : 18,
                                        padding: isMobile ? '16px 32px' : '18px 40px',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        transition: 'all 0.3s ease',
                                        backdropFilter: 'blur(10px)',
                                        minWidth: isMobile ? '100%' : 'auto'
                                    }}
                                    onMouseEnter={e => {
                                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                        e.target.style.borderColor = '#fff';
                                        e.target.style.transform = 'translateY(-3px)';
                                    }}
                                    onMouseLeave={e => {
                                        e.target.style.background = 'transparent';
                                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                                        e.target.style.transform = 'translateY(0px)';
                                    }}
                                >
                                    <Icon name="search" />
                                    Utforska uppdrag
                                </a>
                            </div>

                            {/* Trust indicators */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: isMobile ? 16 : 32,
                                flexWrap: 'wrap',
                                color: 'rgba(255, 255, 255, 0.8)',
                                fontSize: 14
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Icon name="check circle" style={{ color: SUCCESS_COLOR }} />
                                    Alltid gratis
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Icon name="shield alternate" style={{ color: SUCCESS_COLOR }} />
                                    Säkert & pålitligt
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Icon name="clock" style={{ color: SUCCESS_COLOR }} />
                                    2 min att komma igång
                                </div>
                            </div>
                        </div>
                    </AppContainer>
                </section>

                {/* FOOTER */}
                <AppFooter forceShow />
            </main>
        </>
    )
}

export default AboutPage